import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { StoreService } from '@root/shared/service/store.service';
import _ from 'lodash';
import * as moment from 'moment';

interface ICalendarByMonth {
  id: number;
  date: string;
}
interface ICalendarByYear {
  house_id: number;
  year: number;
}
interface IHouseSummary {
  id: number;
}

interface IPrice {
  basic_prices_list?: string;
  weekend_prices_list?: string;
}
interface ISuggestedPrice {
  house_id: number;
  date?: string;
}
interface IHoliday {
  year: string | number;
}
interface ICalendarHouseV2 {
  group_id?: number;
  house_id?: number;
  house_type_id?: number;
  title?: string;
  thirdparty_type?: number;
  physical_status?: number;
  house_cleaning_status?: number;
  page: number;
  page_size: number;
  version?: string;
  type: string;
}
interface ICalendarListV2 {
  house_ids?: string;
  begin: string;
  end: string;
  thirdparty_type?: number;
  version?: string;
  unique_keys?: string;
}
interface IFreeHouseCount {
  group_id?: string;
  begin?: string;
  end?: string;
  version?: string;
}

interface IHousePrice {
  unique_keys?: string;
  thirdparty_type?: number;
  version: string;
}
interface IPriceList {
  house_ids?: Array<number>;
  house_type_ids?: Array<number>;
  begin: string;
  end: string;
  type: string;
}
interface IEditPrice {
  format: string;
  house_ids?: Array<number>;
  house_type_ids?: Array<number>;
  begin: string;
  end: string;
  price_list: Array<any>;
}
interface IOversale {
  house_ids?: string;
  unique_keys?: string;
}
interface IInventoryDiff {
  begin: string;
  end: string;
  house_type_ids: string;
}

interface ISetHouseTypeInventory {
  begin: string;
  end: string;
  house_type_id: string;
}
interface IDeleteNote {
  house_id: number;
  date_list: string;
}
interface IAddNote extends IDeleteNote {
  note: string;
}

interface IHttpResponse {
  error_code: number;
  error_msg: string;
  data: {
    house?: any;
    list?: Array<any>;
    currency?: string;
    daily_price?: number;
    holiday_list?: Array<any>;
    [propName: string]: any;
  };
}

const HOUSE_CALENDAR_API = '/api/calendar/house_calendar';
const HOUSE_CALENDAR_YEAR_API = '/api/calendar/almanac';
const HOUSE_SUMMARY_API = '/api/calendar/house_summary';
const EDIT_BASIC_PRICE_API = '/api/calendar/edit_basic_price';
const EDIT_WEEKEND_PRICE_API = '/api/calendar/edit_weekend_price';
const AIRBNB_SUGGESTED_PRICE_API = '/api/calendar/airbnb_suggested_price';
const EXCHANGE_RAGE_API = '/api/calendar/quote_map';
const HOLIDAY_LIST_API = '/api/calendar/holiday';
const HOUSE_CALENDAR_THIRDPART_API = '/api/calendar/had_thirdparty';
// 查看是否有日历的新列表
const HOUSE_CALENDAR_NEW_LIST_API = '/api/calendar/new_list';
const HOUSE_LIST_V2_API = '/api/calendar/left_house';
const CALNEDAR_LIST_V2_API = '/api/calendar/optimized_list';
const FREE_HOUSE_COUNT_API = '/api/calendar/free_house_count';
const HOUSE_PRICE_API = '/api/calendar/houses_price';
const PRICE_LIST_API = '/api/calendar/edit_daily_price_list';
const EDIT_PRICE_BATCH_API = '/api/calendar/house_daily_price';
const OVERSALE_INFO_API = '/api/calendar/overbooking_info';
const HOUSE_AND_TYPE_INFO_API = '/api/house/get_house_type_and_house';
const INVENTORY_DIFF_API = '/api/calendar/query_inventory_diff';
const SET_HOUSETYPE_INVENTORY_API = '/api/calendar/set_house_type_inventory';
const ADD_NOTE_API = '/api/calendar/add_note';
const DELETE_NOTE_API = '/api/calendar/delete_note';
const TODAY_INFO_API = '/api/calendar/today_info';

/**
 * @deprecated
 */
@Injectable({
  providedIn: 'root',
})
export class HttpCalendarService {
  private readonly holidayYear = moment().year();
  private readonly holidayKey = `hostex_holiday_${this.holidayYear}`;

  constructor(private storeService: StoreService, private http: HttpClient) {}

  // getHolidayList(params: IHoliday) {
  //   const query = new HttpParams().append("year", `${params.year}`);
  //   return this.http.get(HOLIDAY_LIST_API, { params: query }) as Observable<
  //     IHttpResponse
  //   >;
  // }

  getHouseListV2(params) {
    // const query = new HttpParams()
    //   .append("group_id", `${params.group_id}`)
    //   .append("title", `${params.title}`)
    //   .append("thirdparty_type", `${params.thirdparty_type}`)
    //   .append("page", `${params.page}`)
    //   .append("page_size", `${params.page_size}`)
    //   .append("version", params.version)
    //   .append("type", params.type)
    //   .append("physical_status", `${params.physical_status}`)
    //   .append("house_cleaning_status", `${params.house_cleaning_status}`)
    //   .append("house_id", `${params.house_id}`)
    //   .append("house_type_id", `${params.house_type_id}`);
    const query = new HttpParams({
      fromObject: params,
    });
    return this.http.get(HOUSE_LIST_V2_API, { params: query }) as Observable<IHttpResponse>;
  }

  getCalendarListV2(params: ICalendarListV2) {
    const query = new HttpParams()
      .append('house_ids', `${params.house_ids}`)
      .append('begin', `${params.begin}`)
      .append('end', `${params.end}`)
      .append('thirdparty_type', `${params.thirdparty_type}`)
      .append('version', params.version)
      .append('unique_keys', params.unique_keys);

    return this.http.get(CALNEDAR_LIST_V2_API, { params: query }) as Observable<IHttpResponse>;
  }

  fetchPriceCalendarV3(params) {
    return this.http.get('/api/calendar/v3_price_calendar', { params });
  }

  fetchCalendarV3(params) {
    return this.http.get('/api/calendar/v3_listing_calendar', { params });
  }

  getHouseAndTypeInfo(params: { house_id?: number }) {
    const query = new HttpParams().append('house_id', `${params.house_id}`);

    return this.http.get(HOUSE_AND_TYPE_INFO_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  getPriceList(params: IPriceList) {
    return this.http.post(PRICE_LIST_API, params) as Observable<IHttpResponse>;
  }

  getHouseCalendarThirdparty(params: { house_id?: number }) {
    const query = new HttpParams().append('house_id', `${params.house_id}`);
    return this.http.get(HOUSE_CALENDAR_THIRDPART_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  getFreeHouseCount(params: IFreeHouseCount) {
    const query = new HttpParams()
      .append('group_id', `${params.group_id}`)
      .append('begin', params.begin)
      .append('end', params.end)
      .append('version', params.version);
    return this.http.get(FREE_HOUSE_COUNT_API, { params: query }) as Observable<IHttpResponse>;
  }

  getHousesPrice(params: IHousePrice) {
    const query = new HttpParams()
      .append('unique_keys', `${params.unique_keys}`)
      .append('thirdparty_type', `${params.thirdparty_type}`)
      .append('version', `${params.version}`);
    return this.http.get(HOUSE_PRICE_API, { params: query }) as Observable<IHttpResponse>;
  }

  editHousePriceBatch(params: IEditPrice) {
    return this.http.post(EDIT_PRICE_BATCH_API, params) as Observable<IHttpResponse>;
  }

  fetchOversaleInfo(params: IOversale) {
    const query = new HttpParams()
      .append('house_ids', `${params.house_ids}`)
      .append('unique_keys', `${params.unique_keys}`);
    return this.http.get(OVERSALE_INFO_API, { params: query });
  }

  fetchInventoryDiffInfo(params: IInventoryDiff) {
    const query = new HttpParams()
      .append('begin', params.begin)
      .append('end', params.end)
      .append('house_type_ids', params.house_type_ids);

    return this.http.get(INVENTORY_DIFF_API, { params: query });
  }

  setHouseTypeInventory(params: ISetHouseTypeInventory) {
    return this.http.post(SET_HOUSETYPE_INVENTORY_API, params);
  }
  addNote(params: IAddNote) {
    return this.http.post(ADD_NOTE_API, params);
  }

  deleteNote(params: IDeleteNote) {
    return this.http.post(DELETE_NOTE_API, params);
  }

  fetchTodayInfo() {
    return this.http.get(TODAY_INFO_API);
  }

  /**
   * {
   *    2019-11-11: {
   *     name: '光棍节',
   *     isLegalHoliday: false, 法定假日，“休”
   *     isOff: false, // 调休，“班”
   *   }
   * }
   */
  fetchHolidayList() {
    // const year = moment().year();
    // const storageKey = `hostex_holiday_${year}`;
    this.http.get('/api/calendar/holiday', { params: { year: this.holidayYear } }).subscribe((res) => {
      // this.getHolidayList({ year: this.holidayYear }).subscribe((res) => {
      const list = _.get(res, 'data.holiday_list') || [];
      this.storeHolidayToLocal(list);
      // const listOfHoliday = {};
      // _.forEach(list, holiday => {
      //   const festival = moment(holiday.festival).format("YYYY-MM-DD");
      //   listOfHoliday[festival] = { name: holiday.name, isLegalHoliday: false };

      //   _.forEach(holiday.list, date => {
      //     if (+date.status === 1) {
      //       const current = moment(date.date).format("YYYY-MM-DD");
      //       listOfHoliday[current] =
      //         current === festival
      //           ? { name: holiday.name, isLegalHoliday: true }
      //           : { isLegalHoliday: true };
      //     }
      //   });
      // });
      // this.storeService.set(storageKey, listOfHoliday, 2);
    });
  }

  private storeHolidayToLocal(list = []) {
    const listOfHoliday = {};
    _.forEach(list, (holiday) => {
      const festival = moment(holiday.festival).format('YYYY-MM-DD');
      if (!listOfHoliday[festival]) {
        listOfHoliday[festival] = { name: holiday.name, isLegalHoliday: false };
      }

      _.forEach(holiday.list, (date) => {
        if (+date.status === 1) {
          const current = moment(date.date).format('YYYY-MM-DD');
          listOfHoliday[current] =
            current === festival
              ? holiday.name === '班' || holiday.name === 'W'
                ? { isOff: true }
                : { name: holiday.name, isLegalHoliday: true }
              : { isLegalHoliday: true };
        }
      });
    });
    this.storeService.set(this.holidayKey, listOfHoliday, 2);
  }

  getHolidayData() {
    return this.storeService.get(this.holidayKey, 2);
  }
}
